/**
 * Button Component
 * Reusable button with multiple variants, sizes, and states
 * Features: loading state with spinner, accessibility, walrus dark theme
 */

import React, { useEffect } from 'react';
import { ButtonProps } from '@/types';
import { logger } from '@/utils/logger';

/**
 * Button component with multiple visual variants and sizes
 * Supports loading state with integrated spinner and comprehensive ARIA attributes
 *
 * @component
 * @example
 * // Primary button with click handler
 * <Button onClick={() => console.log('Clicked')}>
 *   Click Me
 * </Button>
 *
 * @example
 * // Danger button with loading state
 * <Button variant="danger" loading={isDeleting}>
 *   Delete Item
 * </Button>
 *
 * @param props - ButtonProps interface
 * @returns JSX.Element
 */
const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  onClick,
  disabled = false,
  type = 'button',
  className = '',
  fullWidth = false,
  loading = false,
}) => {
  // Debug logging on render
  useEffect(() => {
    logger.debug(`Button rendered`, {
      variant,
      size,
      disabled,
      loading,
      fullWidth,
    });
  }, [variant, size, disabled, loading, fullWidth]);

  // Variant styles
  const variantStyles: Record<string, string> = {
    primary:
      'bg-white text-walrus-black hover:bg-walrus-white disabled:bg-walrus-gray disabled:text-walrus-silver',
    secondary:
      'bg-walrus-charcoal text-walrus-white border border-walrus-gray hover:bg-walrus-gray disabled:bg-walrus-charcoal disabled:text-walrus-silver',
    ghost:
      'bg-transparent text-walrus-white hover:bg-walrus-charcoal disabled:text-walrus-silver',
    danger:
      'bg-red-600 text-walrus-white hover:bg-red-700 disabled:bg-walrus-gray disabled:text-walrus-silver',
  };

  // Size styles (padding and text size)
  const sizeStyles: Record<string, string> = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  // Combined class names
  const buttonClasses = [
    'inline-flex',
    'items-center',
    'justify-center',
    'gap-2',
    'font-medium',
    'rounded-lg',
    'transition-all',
    'duration-200',
    'ease-in-out',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'focus:ring-offset-walrus-black',
    'focus:ring-white',
    variantStyles[variant],
    sizeStyles[size],
    fullWidth && 'w-full',
    (disabled || loading) && 'cursor-not-allowed opacity-60',
    className,
  ].filter(Boolean);

  /**
   * Handle button click with debug logging
   */
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    logger.debug(`Button clicked`, {
      variant,
      size,
      disabled,
      loading,
    });

    if (onClick && !disabled && !loading) {
      onClick(event);
    }
  };

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={disabled || loading}
      className={buttonClasses.join(' ')}
      aria-busy={loading}
      aria-disabled={disabled || loading}
      data-testid={`button-${variant}-${size}`}
    >
      {/* Spinner when loading */}
      {loading && (
        <div
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-label="Loading"
          role="status"
        />
      )}

      {/* Button content */}
      {children}
    </button>
  );
};

Button.displayName = 'Button';

export default Button;
